import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { setRequestStatus } from '../../application/actions/miscActions';
import {
    clearExploreState,
    exploreMerchant
} from '../../application/actions/exploreActions';
import { getMerchantDetails } from '../../application/actions/merchantActions';
import { getMerchantDetailsAPI } from '../../infrastructure/services/api/Merchant';
import { t } from '../../miscellaneous/language';

import { 
    useDidMount
} from '../../application/hooks';

import './Explore.css';
import MerchantResult from './MerchantResult';
import ItemResult from './ItemResult';

const MIN_SEARCH_LENGTH = 2;
const INITIAL_VISIBLE_GROUPS = 6;
const LOAD_MORE_GROUPS = 4;
const MIN_VISIBLE_ITEMS = 3;

const getMerchantId = (merchant) => merchant?.id || merchant?.merchants_id;
const getItemMerchantId = (item) => item?.merchants?.id || item?.merchants_id;

const normalizeMerchant = (merchant, fallback = {}) => ({
    ...fallback,
    ...merchant,
    id: getMerchantId(merchant) || getMerchantId(fallback),
    is_closed: merchant?.is_closed ?? fallback?.is_closed ?? 0,
    partner_level: merchant?.partner_level ?? fallback?.partner_level ?? 0,
});

const groupResultsByMerchant = (merchants, items) => {
    const merchantGroups = new Map();

    merchants.forEach((merchant, index) => {
        const merchantId = getMerchantId(merchant) || `merchant-index-${index}`;

        merchantGroups.set(merchantId, {
            id: merchantId,
            items: [],
            merchant: normalizeMerchant(merchant),
        });
    });

    items.forEach((item, index) => {
        const merchantId = getItemMerchantId(item) || `item-merchant-index-${index}`;
        const existingGroup = merchantGroups.get(merchantId);
        const merchant = normalizeMerchant(item.merchants, existingGroup?.merchant);

        if (existingGroup) {
            merchantGroups.set(merchantId, {
                ...existingGroup,
                merchant,
                items: [...existingGroup.items, item],
            });
        } else {
            merchantGroups.set(merchantId, {
                id: merchantId,
                merchant,
                items: [item],
            });
        }
    });

    return Array.from(merchantGroups.values());
}

const extractMerchantItems = (merchantDetails) => {
    const details = Array.isArray(merchantDetails) ? merchantDetails[0] : merchantDetails;
    const categoryItems = details?.merchant_item_category?.flatMap(
        (category) => category.items || []
    ) || [];

    return details?.merchant_items || categoryItems;
}

const getRandomItems = (items, count, excludedIds = []) => {
    const excludedIdSet = new Set(excludedIds);

    return items
        .filter((item) => !excludedIdSet.has(item.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, count);
}

const Explore = ({detailFunction}) => {
    const didMount = useDidMount();
    const dispatch = useDispatch();
    const history = useHistory();
    const loadMoreRef = React.useRef(null);
    const pendingSupplementalFetches = React.useRef(new Set());

    const store = useSelector((state) => ({
        explore: state.explore,
        requestStatus: state.app.requestStatus,
        keyword: state.explore.keyword,
        isLoading: state.app.loading,
    }), shallowEqual);
    const lastFetchedKeyword = React.useRef('');
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_GROUPS);
    const [supplementalItemsByMerchant, setSupplementalItemsByMerchant] = React.useState({});
    const [showBackToTop, setShowBackToTop] = React.useState(false);

    React.useEffect(() => {
        const keyword = (store.keyword || '').trim().toLowerCase();

        if (!didMount || keyword === lastFetchedKeyword.current) {
            return;
        }

        if (keyword.length < MIN_SEARCH_LENGTH) {
            lastFetchedKeyword.current = '';
            dispatch(clearExploreState());
            return;
        }

        lastFetchedKeyword.current = keyword;
        dispatch(exploreMerchant(keyword));
    }, [didMount, store.keyword, dispatch]);
    
    React.useEffect(() => () => {
        dispatch(setRequestStatus(''));
    }, [dispatch]);

    React.useEffect(() => {
        pendingSupplementalFetches.current.clear();
        setSupplementalItemsByMerchant({});
    }, [store.keyword]);

    const merchantGroups = React.useMemo(() => {
        const merchantResults = store.explore.merchants?.data || [];
        const itemResults = store.explore.items?.data || [];

        return groupResultsByMerchant(merchantResults, itemResults);
    }, [store.explore.merchants?.data, store.explore.items?.data]);
    const visibleResults = React.useMemo(
        () => merchantGroups.slice(0, visibleCount),
        [merchantGroups, visibleCount]
    );

    const hasKeyword = (store.keyword || '').trim().length >= MIN_SEARCH_LENGTH;
    const hasResults = merchantGroups.length > 0;
    const hasMoreResults = visibleCount < merchantGroups.length;
    const totalItems = merchantGroups.reduce((total, group) => total + group.items.length, 0);

    React.useEffect(() => {
        setVisibleCount(INITIAL_VISIBLE_GROUPS);
    }, [store.keyword, merchantGroups.length]);

    React.useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 240);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const loadMoreResults = React.useCallback(() => {
        setVisibleCount((currentCount) => (
            Math.min(currentCount + LOAD_MORE_GROUPS, merchantGroups.length)
        ));
    }, [merchantGroups.length]);

    React.useEffect(() => {
        if (!hasMoreResults || !loadMoreRef.current) {
            return undefined;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                loadMoreResults();
            }
        }, {
            rootMargin: '160px 0px'
        });

        observer.observe(loadMoreRef.current);

        return () => observer.disconnect();
    }, [hasMoreResults, loadMoreResults, visibleResults.length]);

    React.useEffect(() => {
        visibleResults.forEach((group) => {
            const merchantId = getMerchantId(group.merchant);
            const neededItems = MIN_VISIBLE_ITEMS - group.items.length;

            if (
                neededItems <= 0 ||
                !merchantId ||
                supplementalItemsByMerchant[merchantId] ||
                pendingSupplementalFetches.current.has(merchantId)
            ) {
                return;
            }

            pendingSupplementalFetches.current.add(merchantId);

            getMerchantDetailsAPI(merchantId)
                .then((response) => {
                    const merchantItems = extractMerchantItems(response.result);
                    const supplementalItems = getRandomItems(
                        merchantItems,
                        neededItems,
                        group.items.map((item) => item.id)
                    );

                    setSupplementalItemsByMerchant((prevState) => ({
                        ...prevState,
                        [merchantId]: supplementalItems,
                    }));
                })
                .catch(() => {
                    setSupplementalItemsByMerchant((prevState) => ({
                        ...prevState,
                        [merchantId]: [],
                    }));
                })
                .finally(() => {
                    pendingSupplementalFetches.current.delete(merchantId);
                });
        });
    }, [supplementalItemsByMerchant, visibleResults]);

    const goToMerchantDetail = React.useCallback((merchant) => {
        if (!merchant?.id) {
            return;
        }

        dispatch(getMerchantDetails(merchant.id));
        history.push(`/merchant/${merchant.id}`);
    }, [dispatch, history]);

    if (!hasKeyword) {
        return (
            <div className="explore-state-container">
                <span className="explore-state-title">{t.search.initialTitle}</span>
                <span className="explore-state-desc">{t.search.initialDesc}</span>
            </div>
        )
    }

    if (store.isLoading) {
        return (
            <div className="explore-state-container">
                <span className="explore-state-title">{t.search.searchingTitle}</span>
                <span className="explore-state-desc">{t.search.searchingDesc}</span>
            </div>
        );
    }

    if (store.requestStatus || !hasResults) {
        return (
            <div className="explore-state-container">
                <span className="explore-state-title">{t.search.noResultTitle}</span>
                <span className="explore-state-desc">{t.search.noResultDesc}</span>
            </div>
        );
    }

    return (
        <div className="explore-merchant-list-container">
            <div className="explore-result-summary">
                <span>{t.search.merchantFound.replace('{count}', merchantGroups.length)}</span>
                {totalItems > 0 ? <span>{t.search.menuRelated.replace('{count}', totalItems)}</span> : null}
            </div>

            {visibleResults.map((group) => (
                <div className="explore-merchant-group" key={group.id}>
                    <MerchantResult item={group.merchant} />

                    {(() => {
                        const merchantId = getMerchantId(group.merchant);
                        const supplementalItems = supplementalItemsByMerchant[merchantId] || [];
                        const displayedItems = [...group.items, ...supplementalItems].slice(0, Math.max(MIN_VISIBLE_ITEMS, 6));

                        return displayedItems.length > 0 ? (
                        <div className="explore-group-items">
                            {displayedItems.map((item) => (
                                <ItemResult
                                    compact
                                    detailFunction={detailFunction}
                                    item={item}
                                    key={item.id}
                                />
                            ))}
                        </div>
                        ) : null;
                    })()}

                    <button
                        className="explore-merchant-detail-button"
                        onClick={() => goToMerchantDetail(group.merchant)}
                        type="button"
                    >
                        {t.search.merchantDetail}
                    </button>
                </div>
            ))}

            {hasMoreResults ? (
                <button
                    className="explore-load-more"
                    onClick={loadMoreResults}
                    ref={loadMoreRef}
                    type="button"
                >
                    {t.search.showMore}
                </button>
            ) : (
                <div className="explore-feed-end" />
            )}
            {showBackToTop ? (
                <button
                    className="explore-back-to-top"
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label={t.search.backToTop}
                >
                    ↑
                </button>
            ) : null}
        </div>
    )
}

export default Explore;
